package com.klantenstop.web.components

import androidx.compose.runtime.Composable
import com.klantenstop.web.data.models.domain.Company
import com.klantenstop.web.data.models.domain.Status
import com.materialkobweb.styles.MaterialColorVars
import com.klantenstop.web.styles.staticAcceptColor
import com.klantenstop.web.utils.logoImage
import com.klantenstop.web.utils.withAlpha
import com.materialkobweb.components.widgets.DsExternalLinkStyle
import com.materialkobweb.components.widgets.DsMaterialSymbols
import com.materialkobweb.components.widgets.IconButton
import com.varabyte.kobweb.browser.dom.ElementTarget
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.ObjectFit
import com.varabyte.kobweb.compose.css.OverflowWrap
import com.varabyte.kobweb.compose.css.UserSelect
import com.varabyte.kobweb.compose.css.WhiteSpace
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.alignSelf
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.border
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.bottom
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxSize
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.lineHeight
import com.varabyte.kobweb.compose.ui.modifiers.maxWidth
import com.varabyte.kobweb.compose.ui.modifiers.objectFit
import com.varabyte.kobweb.compose.ui.modifiers.onClick
import com.varabyte.kobweb.compose.ui.modifiers.overflowWrap
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.position
import com.varabyte.kobweb.compose.ui.modifiers.right
import com.varabyte.kobweb.compose.ui.modifiers.userSelect
import com.varabyte.kobweb.compose.ui.modifiers.whiteSpace
import com.varabyte.kobweb.compose.ui.modifiers.zIndex
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.navigation.Anchor
import com.varabyte.kobweb.navigation.OpenLinkStrategy
import com.varabyte.kobweb.silk.components.forms.ButtonSize
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.overlay.PopupPlacement
import com.varabyte.kobweb.silk.components.overlay.Tooltip
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import com.varabyte.kobweb.silk.theme.shapes.Circle
import com.varabyte.kobweb.silk.theme.shapes.clip
import org.jetbrains.compose.web.css.AlignSelf
import org.jetbrains.compose.web.css.CSSColorValue
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.Position
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Text

@Composable
fun KsCompanyLogo(
    company: Company,
    modifier: Modifier = Modifier,
    statusIconSize: StatusIconSize = StatusIconSize.Md
) {
    Box(
        modifier = modifier
            .userSelect(UserSelect.None)
            .alignSelf(AlignSelf.Center)
            .position(Position.Relative)
            .borderRadius(10.px)
            .backgroundColor(MaterialColorVars.SurfaceContainerLow.value())
            .border {
                width(1.px)
                color(MaterialColorVars.Outline.withAlpha(0.5f))
                style(LineStyle.Solid)
            },
        contentAlignment = Alignment.Center
    ) {
        val imagePadding = when {
            company.logo != null -> 10.px
            company.categories.firstOrNull()?.logo != null -> 20.px
            else -> 10.px
        }

        val logoModifier = Modifier.fillMaxSize().objectFit(ObjectFit.Contain).padding(imagePadding)

        when {
            company.logo?.url != null -> Image(modifier = logoModifier, src = company.logo!!.url)
            company.categories.firstOrNull()?.logo?.url != null -> StyledSvg(
                url = company.categories.first().logo!!.url,
                tint = MaterialColorVars.OnSurface.value(),
                modifier = logoModifier
            )

            else -> Image(modifier = logoModifier, src = logoImage(ColorMode.current))
        }

        KsStatusIcon(
            status = company.status,
            size = statusIconSize,
            modifier = Modifier
                .clip(Circle())
                .position(Position.Absolute)
                .padding(2.px)
                .bottom((-8).px)
                .right((-8).px)
        )
    }
}

enum class StatusIconSize {
    Sm,
    Md,
    Lg
}

@Composable
private fun KsStatusIcon(
    status: Status,
    size: StatusIconSize = StatusIconSize.Md,
    backgroundColor: CSSColorValue = MaterialColorVars.Background.value(),
    modifier: Modifier = Modifier
) {
    val newModifier = when (size) {
        StatusIconSize.Sm -> modifier.fontSize(24.px)
        StatusIconSize.Md -> modifier.fontSize(32.px)
        StatusIconSize.Lg -> modifier.fontSize(40.px)
    }
    DsMaterialSymbols(
        modifier = newModifier
            .backgroundColor(backgroundColor),
        icon = status.status.toMaterialIcon(),
        color = status.status.toColor()
    )
}

@Composable
fun KsVerified(
    modifier: Modifier = Modifier
) {
    Row(
        modifier = modifier.color(staticAcceptColor),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(2.px)
    ) {
        DsMaterialSymbols(
            modifier = Modifier
                .fontSize(14.px),
            weight = 900,
            icon = "check"
        )

        SpanText(
            text = "Verified",
            modifier = Modifier
                .fontSize(12.px)
                .lineHeight(14.px)
                .fontWeight(FontWeight.Medium)
        )
    }
}

@Composable
fun KsStatusUpdatedAt(
    company: Company,
    modifier: Modifier = Modifier
) {
    Row(
        modifier = modifier,
        horizontalArrangement = Arrangement.spacedBy(6.px),
        verticalAlignment = Alignment.CenterVertically
    ) {
        if (company.isStale) {
            IconButton(
                modifier = Modifier.onClick {
                    // Prevent the click from propagating to parent elements
                    it.preventDefault()
                    it.stopPropagation()
                },
                size = ButtonSize.XS,
                onClick = { }
            ) {
                DsMaterialSymbols(
                    icon = "info"
                )
            }

            Tooltip(
                modifier = Modifier
                    .zIndex(10)
                    .maxWidth(250.px)
                    .whiteSpace(WhiteSpace.PreWrap),
                target = ElementTarget.PreviousSibling,
                placement = PopupPlacement.TopLeft,
                text = "Al een tijdje niet meer geupdate, dus de info kan foutief zijn.\n\nHeb je nieuwe informatie ontvangen, dan kan je het zelf altijd updaten."
            )
        }

        SpanText(
            modifier = Modifier
                .fontSize(12.px)
                .lineHeight(18.px)
                .color(MaterialColorVars.Outline.value()),
            text = "Geüpdate op: ${company.updateAtHumanReadable}"
        )
    }
}

@Composable
fun KsExternalLink(
    modifier: Modifier = Modifier,
    text: String,
    materialIcon: String? = null,
    href: String? = null
) {
    @Composable
    fun actual() = Row(
        modifier = modifier
            .overflowWrap(OverflowWrap.BreakWord),
        verticalAlignment = Alignment.CenterVertically
    ) {
        if (materialIcon != null) {
            DsMaterialSymbols(
                modifier = Modifier.fontSize(18.px),
                icon = materialIcon
            )
        }

        Text(text)
    }

    if (href != null) {
        Anchor(
            openExternalLinksStrategy = OpenLinkStrategy.IN_NEW_TAB,
            openInternalLinksStrategy = OpenLinkStrategy.IN_NEW_TAB,
            href = href,
            attrs = DsExternalLinkStyle.toModifier().toAttrs()
        ) {
            actual()
        }
    } else {
        actual()
    }
}
